import { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../lib/utils';
import { useAppSelector } from '../store';
import type { C2Event } from '../types';

interface EventContextValue {
  activeEvent: C2Event | null;
  activeEventId: string | null;
  loading: boolean;
}

const EventContext = createContext<EventContextValue>({
  activeEvent: null,
  activeEventId: null,
  loading: false,
});

export function useActiveEvent() {
  return useContext(EventContext);
}

export function EventProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const { token } = useAppSelector((state) => state.auth);
  const [activeEvent, setActiveEvent] = useState<C2Event | null>(null);
  const [loading, setLoading] = useState(false);

  const eventId = searchParams.get('eventId');
  
  // Persist eventId to sessionStorage to survive window resize/move
  useEffect(() => {
    if (eventId) {
      sessionStorage.setItem('c2_active_event_id', eventId);
    }
  }, [eventId]);
  
  // Fallback to sessionStorage if URL param is lost
  const effectiveEventId = eventId || sessionStorage.getItem('c2_active_event_id');

  useEffect(() => {
    if (!effectiveEventId || !token) {
      setActiveEvent(null);
      return;
    }
    setLoading(true);
    fetch(`${API_BASE_URL}/events/${effectiveEventId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setActiveEvent(d.event || null))
      .catch(() => setActiveEvent(null))
      .finally(() => setLoading(false));
  }, [effectiveEventId, token]);

  return (
    <EventContext.Provider value={{ activeEvent, activeEventId: effectiveEventId, loading }}>
      {children}
    </EventContext.Provider>
  );
}
